import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Calendar, MapPin, Users, Check, Share2, Clock, X } from "lucide-react";
import { formatLongDate, formatTime, formatShortDate } from "@/lib/dateUtils";
import {
  FormEventPublic,
  GoalEventPublic,
  TicketsEventPublic,
  DuesEventPublic,
  StoreEventPublic,
  WaiverEventPublic,
} from "@/components/EventTypeRenderers";
import { EVENT_TYPE_LABELS } from "@/lib/eventTypes";

export default function EventPublic() {
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  // Potluck signup dialog state
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [signedUpSlots, setSignedUpSlots] = useState(() => {
    const cached = localStorage.getItem(`signed_${slug}`);
    return cached ? JSON.parse(cached) : [];
  });
  // Calendar booking dialog
  const [selectedCalSlot, setSelectedCalSlot] = useState(null);
  const [managementToken, setManagementToken] = useState(() => localStorage.getItem(`mtoken_${slug}`) || "");
  const [myBooking, setMyBooking] = useState(null);
  const [rescheduleMode, setRescheduleMode] = useState(false);

  const [form, setForm] = useState({ name: "", email: "", phone: "", quantity: 1, comment: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/events/${slug}`);
      setEvent(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const loadMyBooking = async () => {
    if (!managementToken) return;
    try {
      const { data } = await api.get(`/events/${slug}/booking/${managementToken}`);
      setMyBooking(data);
    } catch {
      // token invalid or booking cancelled → clear it
      localStorage.removeItem(`mtoken_${slug}`);
      setManagementToken("");
      setMyBooking(null);
    }
  };

  useEffect(() => {
    load();
     
  }, [slug]);

  useEffect(() => {
    if (event?.event_type === "calendar" && managementToken) loadMyBooking();
     
  }, [event, managementToken]);

  const isCalendar = event?.event_type === "calendar";
  const isPotluck = event?.event_type === "potluck" || !event?.event_type;
  const isOther = event && !isCalendar && !isPotluck;
  const settings = event?.calendar_settings || {};
  const timeFormat = settings.time_format || "12h";

  // Group calendar slots by date
  const slotsByDate = useMemo(() => {
    if (!isCalendar) return new Map();
    const map = new Map();
    for (const s of event.calendar_slots || []) {
      if (!map.has(s.date)) map.set(s.date, []);
      map.get(s.date).push(s);
    }
    for (const arr of map.values()) arr.sort((a, b) => a.start_time.localeCompare(b.start_time));
    return map;
  }, [event, isCalendar]);

  // Potluck signup handlers
  const openSignup = (slot) => {
    setSelectedSlot(slot);
    setForm({ name: "", email: "", phone: "", quantity: 1, comment: "" });
  };

  const submitSignup = async () => {
    if (!form.name.trim()) return toast.error("Escribe tu nombre");
    setSubmitting(true);
    try {
      await api.post(`/events/${slug}/signup`, {
        slot_id: selectedSlot.id,
        name: form.name,
        email: form.email || null,
        phone: form.phone,
        quantity: parseInt(form.quantity, 10) || 1,
        comment: form.comment,
      });
      toast.success("¡Listo, estás apuntado!");
      const updated = [...signedUpSlots, selectedSlot.id];
      setSignedUpSlots(updated);
      localStorage.setItem(`signed_${slug}`, JSON.stringify(updated));
      setSelectedSlot(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  // Calendar booking handlers
  const openCalSlot = (slot) => {
    // If user already has a booking and clicks another slot, that means reschedule
    if (myBooking && slot.id !== myBooking.slot_id && !slot.booked) {
      setSelectedCalSlot(slot);
      setRescheduleMode(true);
      return;
    }
    if (slot.booked) return;
    setSelectedCalSlot(slot);
    setRescheduleMode(false);
    setForm({ name: "", email: "", phone: "", quantity: 1, comment: "" });
  };

  const submitBooking = async () => {
    if (!form.name.trim()) return toast.error("Escribe tu nombre");
    if (settings.field_email === "required" && !form.email.trim()) return toast.error("El correo es requerido");
    if (settings.field_phone === "required" && !form.phone.trim()) return toast.error("El teléfono es requerido");
    setSubmitting(true);
    try {
      const { data } = await api.post(`/events/${slug}/book`, {
        slot_id: selectedCalSlot.id,
        name: form.name,
        email: form.email || null,
        phone: form.phone,
      });
      localStorage.setItem(`mtoken_${slug}`, data.management_token);
      setManagementToken(data.management_token);
      toast.success("¡Reserva confirmada!");
      setSelectedCalSlot(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const submitReschedule = async () => {
    setSubmitting(true);
    try {
      await api.post(`/events/${slug}/booking/${managementToken}/reschedule`, {
        new_slot_id: selectedCalSlot.id,
      });
      toast.success("Horario actualizado");
      setSelectedCalSlot(null);
      setRescheduleMode(false);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const cancelMyBooking = async () => {
    if (!window.confirm("¿Cancelar tu reserva?")) return;
    try {
      await api.delete(`/events/${slug}/booking/${managementToken}`);
      localStorage.removeItem(`mtoken_${slug}`);
      setManagementToken("");
      setMyBooking(null);
      toast.success("Reserva cancelada");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) await navigator.share({ title: event.title, url });
      else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copiado");
      }
    } catch (_e) {
      // ignore
    }
  };

  if (loading) return <div className="min-h-screen bg-cream-50"><Navbar /><p className="p-10 text-ink-500">Cargando…</p></div>;
  if (!event)
    return (
      <div className="min-h-screen bg-cream-50">
        <Navbar />
        <div className="p-16 text-center">
          <h2 className="font-heading text-3xl text-ink-900">Evento no encontrado</h2>
          <Link to="/" className="text-coral mt-4 inline-block">Volver al inicio</Link>
        </div>
      </div>
    );

  return (
    <div className="min-h-screen bg-cream-50">
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 md:px-10 py-14">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <span className="text-xs uppercase tracking-[0.24em] text-coral">
              {isCalendar ? "Reserva de citas" : isOther ? EVENT_TYPE_LABELS[event.event_type] : event.category}
            </span>
            <h1 className="mt-3 font-heading text-4xl md:text-5xl text-ink-900 font-light leading-tight">
              {event.title}
            </h1>
            <p className="mt-3 text-ink-500">Organiza: {event.organizer_name}</p>
          </div>
          <Button
            variant="outline"
            onClick={share}
            className="rounded-full border-line hover:bg-cream-100"
            data-testid="share-btn"
          >
            <Share2 size={16} strokeWidth={1.75} className="mr-2" /> Compartir
          </Button>
        </div>

        {(event.event_date || event.location) && (
          <div className="mt-6 flex flex-wrap gap-6 text-ink-500">
            {event.event_date && <span className="flex items-center gap-2"><Calendar size={16} strokeWidth={1.75} /> {event.event_date}</span>}
            {event.location && <span className="flex items-center gap-2"><MapPin size={16} strokeWidth={1.75} /> {event.location}</span>}
          </div>
        )}

        {event.description && (
          <p className="mt-8 text-ink-700 leading-relaxed max-w-3xl">{event.description}</p>
        )}

        {/* My booking banner (calendar) */}
        {isCalendar && myBooking && (
          <div className="mt-8 bg-sage/10 border border-sage/30 rounded-2xl p-5 flex flex-wrap items-center justify-between gap-3" data-testid="my-booking-banner">
            <div>
              <div className="text-xs uppercase tracking-widest text-sage-hover">Tu reserva</div>
              <div className="mt-1 font-heading text-lg text-ink-900">
                {formatShortDate(myBooking.date, settings.date_format)} · {formatTime(myBooking.start_time, timeFormat)}
              </div>
              <div className="text-sm text-ink-500">
                Toca otro horario disponible para cambiarlo, o cancela tu reserva.
              </div>
            </div>
            <Button
              variant="outline"
              className="rounded-full border-coral text-coral hover:bg-coral/5"
              onClick={cancelMyBooking}
              data-testid="cancel-my-booking-btn"
            >
              <X size={14} strokeWidth={1.75} className="mr-1.5" /> Cancelar reserva
            </Button>
          </div>
        )}

        {/* Content */}
        {!isCalendar && !isOther && (
          <div className="mt-12">
            <h2 className="font-heading text-2xl text-ink-900 mb-6">Apúntate a lo que puedas traer o hacer</h2>
            <div className="space-y-3">
              {event.slots.map((s, i) => {
                const remaining = s.quantity - s.filled;
                const full = remaining <= 0;
                const alreadySigned = signedUpSlots.includes(s.id);
                return (
                  <div key={s.id} className="bg-white border border-line rounded-2xl p-5 md:p-6 flex flex-wrap items-center justify-between gap-4" data-testid={`slot-item-${i}`}>
                    <div>
                      <h3 className="font-heading text-xl text-ink-900">{s.title}</h3>
                      {s.when && <p className="text-sm text-ink-500 mt-1">{s.when}</p>}
                      <div className="mt-2 flex items-center gap-1.5 text-xs uppercase tracking-widest text-sage">
                        <Users size={12} strokeWidth={1.75} /> {s.filled}/{s.quantity} apuntados
                      </div>
                    </div>
                    {full ? (
                      <span className="text-sm text-ink-500 px-4 py-2 bg-cream-100 rounded-full" data-testid={`slot-full-${i}`}>Cupo lleno</span>
                    ) : alreadySigned ? (
                      <span className="text-sm text-sage px-4 py-2 flex items-center gap-2" data-testid={`slot-signed-${i}`}>
                        <Check size={16} strokeWidth={1.75} /> Ya te apuntaste
                      </span>
                    ) : (
                      <Button onClick={() => openSignup(s)} className="rounded-full bg-coral hover:bg-coral-hover text-white px-6" data-testid={`slot-signup-btn-${i}`}>
                        Apuntarme
                      </Button>
                    )}
                  </div>
                );
              })}
              {event.slots.length === 0 && <p className="text-ink-500">El organizador todavía no ha agregado espacios.</p>}
            </div>
          </div>
        )}

        {isCalendar && (
          <div className="mt-12 space-y-8">
            <h2 className="font-heading text-2xl text-ink-900">
              Elige un horario disponible
            </h2>
            {Array.from(slotsByDate.entries()).map(([date, list]) => (
              <div key={date} className="rounded-2xl overflow-hidden border border-line bg-white shadow-sm" data-testid={`day-block-${date}`}>
                {/* Day header — colored bar (matches user reference screenshot) */}
                <div className="bg-cream-100 border-b border-line px-5 py-3">
                  <h3 className="font-heading text-lg md:text-xl text-ink-900 font-medium">
                    {formatLongDate(date)}
                  </h3>
                </div>
                <ul className="divide-y divide-line">
                  {list.map((s, i) => {
                    const booked = s.booked;
                    const isMine = myBooking && myBooking.slot_id === s.id;
                    return (
                      <li
                        key={s.id}
                        className={`px-5 py-4 flex items-center gap-4 transition-colors ${
                          booked && !isMine ? "bg-cream-100/60" : "bg-white hover:bg-cream-50"
                        } ${isMine ? "bg-sage/10" : ""}`}
                        data-testid={`cal-slot-${date}-${s.start_time}`}
                      >
                        {/* Time in top-left corner style */}
                        <div className="w-20 shrink-0 text-ink-500 font-medium tabular-nums flex items-center gap-1">
                          <Clock size={12} strokeWidth={1.75} className="opacity-60" />
                          <span className="text-sm">{formatTime(s.start_time, timeFormat)}</span>
                        </div>

                        <div className="flex-1 flex items-center justify-between gap-3">
                          <div>
                            {booked ? (
                              <span className={`text-base ${isMine ? "text-sage-hover font-medium" : "text-ink-300"}`}>
                                {isMine ? "Tu reserva" : booked.name}
                              </span>
                            ) : (
                              <span className="text-base text-ink-700">Disponible</span>
                            )}
                          </div>
                          {!booked && !isMine && (
                            <Button
                              size="sm"
                              onClick={() => openCalSlot(s)}
                              className="rounded-full bg-coral hover:bg-coral-hover text-white h-8 px-4 text-xs"
                              data-testid={`cal-signup-${date}-${s.start_time}`}
                            >
                              {myBooking ? "Cambiar aquí" : "Apuntarme"}
                            </Button>
                          )}
                          {isMine && (
                            <span className="text-xs uppercase tracking-widest text-sage-hover">Tú</span>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
            {slotsByDate.size === 0 && <p className="text-ink-500">Todavía no hay horarios disponibles.</p>}
          </div>
        )}

        {isOther && (
          <>
            {event.event_type === "form" && <FormEventPublic event={event} />}
            {(event.event_type === "gift" || event.event_type === "fundraiser") && <GoalEventPublic event={event} onDone={load} />}
            {event.event_type === "tickets" && <TicketsEventPublic event={event} />}
            {event.event_type === "dues" && <DuesEventPublic event={event} />}
            {event.event_type === "store" && <StoreEventPublic event={event} />}
            {event.event_type === "waiver" && <WaiverEventPublic event={event} />}
          </>
        )}
      </div>

      {/* Potluck signup dialog */}
      <Dialog open={!!selectedSlot} onOpenChange={(v) => !v && setSelectedSlot(null)}>
        <DialogContent className="rounded-3xl border-line max-w-md" data-testid="signup-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-ink-900 font-light">
              Apuntarme a &ldquo;{selectedSlot?.title}&rdquo;
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-widest text-ink-500">Tu nombre</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl border-line h-11" data-testid="signup-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-widest text-ink-500">Correo (opcional)</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-xl border-line h-11" data-testid="signup-email-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-widest text-ink-500">Cuántos</Label>
                <Input type="number" min={1} value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="rounded-xl border-line h-11" data-testid="signup-quantity-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-widest text-ink-500">Nota (opcional)</Label>
              <Textarea value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} className="rounded-xl border-line min-h-[70px]" data-testid="signup-comment-input" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={submitSignup} disabled={submitting} className="w-full rounded-full bg-coral hover:bg-coral-hover text-white h-11" data-testid="signup-confirm-btn">
              {submitting ? "Enviando…" : "Confirmar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Calendar booking dialog */}
      <Dialog open={!!selectedCalSlot} onOpenChange={(v) => !v && setSelectedCalSlot(null)}>
        <DialogContent className="rounded-3xl border-line max-w-md" data-testid="cal-signup-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-ink-900 font-light">
              {rescheduleMode ? "Cambiar reserva" : "Confirmar reserva"}
            </DialogTitle>
            {selectedCalSlot && (
              <DialogDescription className="text-ink-500">
                {formatLongDate(selectedCalSlot.date)} · {formatTime(selectedCalSlot.start_time, timeFormat)}
              </DialogDescription>
            )}
          </DialogHeader>
          {rescheduleMode ? (
            <>
              <p className="text-ink-700 text-sm">
                Se cambiará tu reserva actual de{" "}
                <b>{myBooking && formatTime(myBooking.start_time, timeFormat)}</b> al nuevo horario seleccionado.
              </p>
              <DialogFooter>
                <Button onClick={submitReschedule} disabled={submitting} className="w-full rounded-full bg-coral hover:bg-coral-hover text-white h-11" data-testid="reschedule-confirm-btn">
                  {submitting ? "Cambiando…" : "Confirmar cambio"}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <div className="space-y-4 mt-2">
                <div className="space-y-1.5">
                  <Label className="text-[11px] uppercase tracking-widest text-ink-500">
                    Nombre <span className="text-coral">*</span>
                  </Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl border-line h-11" data-testid="cal-name-input" />
                </div>
                {settings.field_email !== "hidden" && (
                  <div className="space-y-1.5">
                    <Label className="text-[11px] uppercase tracking-widest text-ink-500">
                      Correo {settings.field_email === "required" ? <span className="text-coral">*</span> : <span className="text-ink-300">(opcional)</span>}
                    </Label>
                    <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-xl border-line h-11" data-testid="cal-email-input" />
                  </div>
                )}
                {settings.field_phone !== "hidden" && (
                  <div className="space-y-1.5">
                    <Label className="text-[11px] uppercase tracking-widest text-ink-500">
                      Teléfono {settings.field_phone === "required" ? <span className="text-coral">*</span> : <span className="text-ink-300">(opcional)</span>}
                    </Label>
                    <Input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-xl border-line h-11" data-testid="cal-phone-input" />
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button onClick={submitBooking} disabled={submitting} className="w-full rounded-full bg-coral hover:bg-coral-hover text-white h-11" data-testid="cal-confirm-btn">
                  {submitting ? "Reservando…" : "Confirmar reserva"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
